/**
 * Pure per-identity read-state helpers for the record comments panel
 * (operator-task-record-comments-ui-slices-2-3, Slice 2).
 *
 * Two-layer read model (Stefan's option B, 2026-09-08). A per-(record, identity)
 * read-until WATERMARK is the cheap default, plus SPARSE per-comment OVERRIDE
 * rows (`record_comment_read.is_read`) stored ONLY when the viewer's desired
 * state for a single comment differs from what the watermark implies. This keeps
 * read-state storage at O(records × readers) + a handful of overrides — the
 * O(comments × readers) growth of the original per-comment model is gone — while
 * preserving Margot's "literal B" behaviour: nothing auto-marks a comment read,
 * and a read comment can be individually re-flagged unread (an is_read=false
 * override) to keep it on the viewer's still-to-action list.
 *
 * Effective read rule for comment C, viewer I:
 *   read  ⇔  C.author == I
 *         OR (an override row exists for (C, I) AND override.is_read == true)
 *         OR (no override AND C.created_at <= read_until(record, I))
 *
 * A comment the viewer authored is always "read by me" (unread state is not
 * tracked on your own comments).
 *
 * Kept as pure functions (mirroring commentScroll.ts) so the read-state logic is
 * unit-tested independently of the redux/restClient-bound panel component.
 */

/** A viewer's sparse per-comment read override (a `record_comment_read` row). */
export interface CommentReadOverride {
    id: string;
    is_read: boolean;
}

/** Minimal comment shape these helpers read — structurally satisfied by the
 * panel's RecordCommentRow (which also carries body, author name, etc.). */
export interface ReadStateComment {
    author_identity_id: string | null;
    created_at: string;
    /**
     * The viewing identity's read-override row(s) for this comment. The backend
     * `record_comment_read` list select fails closed to the caller's OWN rows,
     * and UNIQUE(comment, identity) bounds it to ≤1 row — so this reverse-list
     * holds at most the viewer's single override.
     */
    record_comment_read_plain_list_record_comment_id: CommentReadOverride[] | null;
}

/**
 * Parse a backend timestamp to epoch milliseconds, tolerating both the
 * space-separated form the API emits ("2026-09-08 20:08:12.7+00:00") and a
 * strict ISO form ("2026-09-08T20:08:12.700Z"). Returns 0 on an empty/unparseable
 * value so a missing watermark never accidentally marks anything read.
 */
export const toEpoch = (ts: string | null | undefined): number => {
    if (!ts) {
        return 0;
    }
    const parsed = Date.parse(ts.replace(" ", "T"));
    return Number.isNaN(parsed) ? 0 : parsed;
};

/** The viewer's override row for this comment, if any (≤1 by construction). */
export const readOverride = (comment: ReadStateComment): CommentReadOverride | undefined =>
    comment.record_comment_read_plain_list_record_comment_id?.[0] ?? undefined;

/** Whether the record-level watermark alone marks this comment read (ignores
 * author + overrides): true when the comment is not newer than the viewer's
 * `read_until`. A missing watermark covers nothing. */
export const isCoveredByWatermark = (
    comment: ReadStateComment,
    readUntil?: string | null
): boolean => {
    if (!readUntil) {
        return false;
    }
    return toEpoch(comment.created_at) <= toEpoch(readUntil);
};

/** Effective read state of a comment for the viewing identity (see file docstring). */
export const isReadByMe = (
    comment: ReadStateComment,
    currentIdentityId?: string,
    readUntil?: string | null
): boolean => {
    if (currentIdentityId && comment.author_identity_id === currentIdentityId) {
        return true;
    }
    const override = readOverride(comment);
    if (override) {
        return override.is_read;
    }
    return isCoveredByWatermark(comment, readUntil);
};

/**
 * The mutation needed to bring a single comment to `desiredRead` for the viewer,
 * keeping overrides SPARSE: an override row is stored ONLY when the desired state
 * differs from what the watermark implies; when they agree, any existing override
 * is removed so the comment falls back to the (cheap) watermark.
 */
export type OverrideAction =
    | { kind: "none" }
    | { kind: "create"; isRead: boolean }
    | { kind: "update"; id: string; isRead: boolean }
    | { kind: "delete"; id: string };

export const resolveOverrideAction = (
    comment: ReadStateComment,
    desiredRead: boolean,
    readUntil?: string | null
): OverrideAction => {
    const covered = isCoveredByWatermark(comment, readUntil);
    const override = readOverride(comment);
    if (desiredRead === covered) {
        // Desired state already matches the watermark → no override needed;
        // drop a now-redundant one to stay sparse.
        return override ? { kind: "delete", id: override.id } : { kind: "none" };
    }
    // Desired state differs from the watermark → an override is required.
    if (override) {
        return override.is_read === desiredRead
            ? { kind: "none" }
            : { kind: "update", id: override.id, isRead: desiredRead };
    }
    return { kind: "create", isRead: desiredRead };
};

/**
 * The id of the FIRST UNREAD comment for the viewing identity — the oldest
 * comment the viewer has not yet read — or undefined when every comment is read
 * (or the thread is empty). Assumes `comments` is in chronological order
 * (oldest→newest), as the panel fetches it (`order: "+created_at"`), so the first
 * match is the oldest unread. Pure wrapper over isReadByMe so the comments-tab
 * "scroll to the first unread comment on open" anchor is unit-tested
 * independently of the redux/restClient-bound panel
 * (operator-task-fe-comments-tab-scroll-to-first-unread).
 */
export const firstUnreadCommentId = <C extends ReadStateComment & { id: string }>(
    comments: readonly C[],
    currentIdentityId?: string,
    readUntil?: string | null
): string | undefined =>
    comments.find((comment) => !isReadByMe(comment, currentIdentityId, readUntil))?.id;

/**
 * Accessibility attributes for the per-comment unread dot. The dot element is
 * ALWAYS rendered (its box stays reserved so marking a comment read causes no
 * layout shift — operator-task-fe-unread-comment-marker-no-layout-shift); only
 * its appearance and its accessible meaning change with the read state: an
 * unread dot is announced as "Unread", a read (transparent) dot is hidden from
 * assistive technology.
 */
export const unreadDotA11yProps = (
    isUnread: boolean
): { title?: string; "aria-label"?: string; "aria-hidden"?: boolean } =>
    isUnread ? { title: "Unread", "aria-label": "Unread" } : { "aria-hidden": true };
